import { useState, useEffect } from 'react';
import articleRepository from '../../../../domain/repositories/ArticleRepository';

/**
 * Custom hook to fetch article details with related articles
 * @param {string|number} articleId - Article ID from URL params
 * @returns {Object} Object containing article, related articles, loading state, and error
 */
export const useArticleDetail = (articleId) => {
  const [data, setData] = useState({
    article: null,
    relatedArticles: [],
    author: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) {
        setError('Article ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const articleData = await articleRepository.getArticleWithRelated(articleId, 3);
        setData(articleData);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err.message || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId]);

  return { ...data, loading, error };
};

/**
 * Custom hook to fetch only article details without related articles
 * @param {string|number} articleId - Article ID
 * @returns {Object} Object containing article, loading state, and error
 */
export const useArticle = (articleId) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) {
        setError('Article ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const articleData = await articleRepository.getArticleById(articleId);
        setArticle(articleData);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err.message || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId]);

  return { article, loading, error };
};

/**
 * Custom hook to fetch latest articles
 * @param {number} limit - Number of articles to fetch
 * @returns {Object} Object containing articles, loading state, and error
 */
export const useLatestArticles = (limit = 4) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const articlesData = await articleRepository.getLatestArticles(limit);
        setArticles(articlesData);
      } catch (err) {
        console.error('Error fetching latest articles:', err);
        setError(err.message || 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [limit]);

  return { articles, loading, error };
};

/**
 * Custom hook to fetch paginated articles
 * @param {number} page - Page number (0-indexed)
 * @returns {Object} Object containing articles, pagination info, loading state, and error
 */
export const usePaginatedArticles = (page = 0) => {
  const [data, setData] = useState({
    articles: [],
    pagination: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [articles, pagination] = await Promise.all([
          articleRepository.getArticlesForPage(page),
          articleRepository.getPaginationInfo(page)
        ]);

        setData({ articles, pagination });
      } catch (err) {
        console.error('Error fetching paginated articles:', err);
        setError(err.message || 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [page]);

  return { ...data, loading, error };
};

/**
 * Custom hook to search articles
 * @param {string} searchTerm - Search term
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {Object} Object containing articles, loading state, and error
 */
export const useArticleSearch = (searchTerm, debounceMs = 300) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setArticles([]);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        
        const results = await articleRepository.searchArticles(searchTerm);
        setArticles(results);
      } catch (err) {
        console.error('Error searching articles:', err);
        setError(err.message || 'Failed to search articles');
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  return { articles, loading, error };
};
